/**
 * Internalized geometry utilities from `features/geometry` and `utils/math`.
 *
 * These are pure functions used by AssetViz's surface clipping and polygon
 * wrapping logic. They have no external dependencies beyond the `LineDirectionX`
 * and `LineDirectionY` enums from the shared `types` module.
 */
import { LineDirectionX, LineDirectionY } from 'types'

// ---------------------------------------------------------------------------
// Boundary constants
// ---------------------------------------------------------------------------

export const BOUNDS_U = [0, 1]
export const BOUNDS_V = [0, 1]

// ---------------------------------------------------------------------------
// Boundary segment checks
// ---------------------------------------------------------------------------

export const segmentAlongXBoundary = (startU: number, endU: number): boolean =>
  startU === endU && (startU === BOUNDS_U[1] || startU === BOUNDS_U[0])

export const segmentAlongYBoundary = (startV: number, endV: number): boolean =>
  startV === endV && (startV === BOUNDS_V[1] || startV === BOUNDS_V[0])

// ---------------------------------------------------------------------------
// Boundary crossing checks
// ---------------------------------------------------------------------------

export const crossesXBoundary = (
  startU: number,
  endU: number | undefined,
  xDirection: LineDirectionX
): boolean => {
  if (endU === undefined || segmentAlongXBoundary(startU, endU)) return false
  return (
    xDirection !== LineDirectionX.None &&
    (startU === endU || startU < endU !== (xDirection === LineDirectionX.Right))
  )
}

export const crossesYBoundary = (
  startV: number,
  endV: number | undefined,
  yDirection: LineDirectionY
): boolean => {
  if (endV === undefined || segmentAlongYBoundary(startV, endV)) return false
  return (
    yDirection !== LineDirectionY.None &&
    (startV === endV || startV < endV !== (yDirection === LineDirectionY.Up))
  )
}

// ---------------------------------------------------------------------------
// Line interpolation (from utils/math)
// ---------------------------------------------------------------------------

/**
 * Given a y value within the range of a line segment, calculates the associated
 * x value along the line.
 */
export function xAlongLine(
  p1: [number, number],
  p2: [number, number],
  y: number
): number {
  const [x1, y1] = p1
  const [x2, y2] = p2
  if (y2 === y1) return x1
  return ((y - y1) / (y2 - y1)) * (x2 - x1) + x1
}

/**
 * Given an x value within the range of a line segment, calculates the associated
 * y value along the line.
 */
export function yAlongLine(
  p1: [number, number],
  p2: [number, number],
  x: number
): number {
  const [x1, y1] = p1
  const [x2, y2] = p2
  if (x2 === x1) return y1
  return ((x - x1) / (x2 - x1)) * (y2 - y1) + y1
}

// ---------------------------------------------------------------------------
// Polygon wrapping (from features/geometry/polygonWrappingUtils)
// ---------------------------------------------------------------------------

/**
 * If the polygon defined by uvs wraps, then create a polygon for the left
 * (or bottom in y direction) side and right (or top in y direction).
 */
export const createPolygonsForWrapping = (
  uvs: number[],
  lineXDirections: ReadonlyArray<LineDirectionX>,
  lineYDirections: ReadonlyArray<LineDirectionY>,
  wrapsX: boolean,
  wrapsY: boolean
): number[][] => {
  if (!wrapsX && !wrapsY) return [uvs]

  let polygonDoesntWrap = true
  let startsOnBottomOrLeft = true
  for (let i = 0; i < uvs.length - 2; i = i + 2) {
    const sx = uvs[i]
    const sy = uvs[i + 1]
    const ex = uvs[i + 2]
    const ey = uvs[i + 3]
    const xDirection = lineXDirections[Math.floor(i / 2)]
    const yDirection = lineYDirections[Math.floor(i / 2)]
    const lineIntersects = wrapsX
      ? crossesXBoundary(sx, ex, xDirection)
      : crossesYBoundary(sy, ey, yDirection)
    if (lineIntersects) {
      polygonDoesntWrap = false
      startsOnBottomOrLeft = wrapsX
        ? xDirection === LineDirectionX.Right
        : yDirection === LineDirectionY.Up
      break
    }
  }
  if (polygonDoesntWrap) return [uvs]

  const polygon: number[] = []
  const partnerPolygon: number[] = []

  let currentlyOnBottomOrLeft = startsOnBottomOrLeft

  for (let i = 0; i < uvs.length; i = i + 2) {
    const u = uvs[i]
    const v = uvs[i + 1]
    const xDirection = lineXDirections[Math.floor(i / 2)]
    const yDirection = lineYDirections[Math.floor(i / 2)]

    const partnerU = wrapsX
      ? currentlyOnBottomOrLeft
        ? u - BOUNDS_U[1]
        : u + BOUNDS_U[1]
      : u
    const partnerV = wrapsY
      ? currentlyOnBottomOrLeft
        ? v - BOUNDS_V[1]
        : v + BOUNDS_V[1]
      : v
    if (currentlyOnBottomOrLeft === startsOnBottomOrLeft) {
      polygon.push(u, v)
      partnerPolygon.push(partnerU, partnerV)
    } else {
      polygon.push(partnerU, partnerV)
      partnerPolygon.push(u, v)
    }
    const lineIntersects = wrapsX
      ? crossesXBoundary(u, uvs.at(i + 2), xDirection)
      : crossesYBoundary(v, uvs.at(i + 3), yDirection)
    if (lineIntersects) currentlyOnBottomOrLeft = !currentlyOnBottomOrLeft
  }
  return [polygon, partnerPolygon]
}
